import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  CreditCard as CardIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../contexts/AuthContext';

const PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_ID ?? '';

const FEATURES = [
  'Unlimited projects',
  'Client & contractor messaging',
  'Milestone tracking & approvals',
  'Document storage',
  'Priority support',
];

export function Subscribe() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useAuth();
  const { status, periodEnd, isActive, loading, startCheckout, openBillingPortal, cancelSubscription } =
    useSubscription();

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Detect redirect back from Stripe Checkout
  useEffect(() => {
    if (searchParams.get('session_id')) {
      setShowSuccess(true);
    }
  }, [searchParams]);

  const handleSubscribe = async () => {
    if (!PRICE_ID) {
      alert('No price configured — set REACT_APP_STRIPE_PRICE_ID in your .env');
      return;
    }
    setCheckoutLoading(true);
    await startCheckout(PRICE_ID);
    setCheckoutLoading(false);
  };

  const handleManage = async () => {
    setCheckoutLoading(true);
    await openBillingPortal();
    setCheckoutLoading(false);
  };

  const handleCancel = async () => {
    setCancelError(null);
    setCancelLoading(true);
    const result = await cancelSubscription();
    setCancelLoading(false);
    if ('error' in result) {
      setCancelError(result.error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
      }}
    >
      <Stack spacing={4} sx={{ width: '100%', maxWidth: 480 }}>

        {/* Success banner */}
        {showSuccess && (
          <Alert severity="success" icon={<CheckIcon />}>
            You're subscribed! Welcome to BuildBridge.{' '}
            <Button size="small" onClick={() => navigate('/')}>
              Go to dashboard
            </Button>
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {isActive ? 'Your Subscription' : 'Get Started'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isActive
              ? `You have full access to BuildBridge.`
              : 'Subscribe to unlock all features for your team.'}
          </Typography>
        </Box>

        {/* Plan card */}
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    BuildBridge Pro
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Everything you need to manage construction projects
                  </Typography>
                </Box>
                {isActive && (
                  <Chip
                    label={status === 'trialing' ? 'Trial' : 'Active'}
                    color="success"
                    size="small"
                  />
                )}
              </Box>

              <Divider />

              <Stack spacing={1.5}>
                {FEATURES.map((feature) => (
                  <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckIcon color="success" fontSize="small" />
                    <Typography variant="body2">{feature}</Typography>
                  </Box>
                ))}
              </Stack>

              <Divider />

              {/* Active subscription info */}
              {isActive && periodEnd && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {status === 'trialing' ? 'Trial ends' : 'Next billing date'}:{' '}
                    <strong>{periodEnd.toLocaleDateString()}</strong>
                  </Typography>
                </Box>
              )}

              {cancelError && <Alert severity="error">{cancelError}</Alert>}

              {/* CTA buttons */}
              {!isActive ? (
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleSubscribe}
                  disabled={checkoutLoading}
                  startIcon={checkoutLoading ? <CircularProgress size={18} /> : <CardIcon />}
                >
                  {checkoutLoading ? 'Redirecting…' : 'Subscribe — 14-day free trial'}
                </Button>
              ) : (
                <Stack spacing={1}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleManage}
                    disabled={checkoutLoading}
                    startIcon={checkoutLoading ? <CircularProgress size={18} /> : <CardIcon />}
                  >
                    {checkoutLoading ? 'Opening portal…' : 'Manage billing'}
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    fullWidth
                    onClick={handleCancel}
                    disabled={cancelLoading}
                  >
                    {cancelLoading ? 'Canceling…' : 'Cancel subscription'}
                  </Button>
                </Stack>
              )}

              {!isActive && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  No credit card required during trial. Cancel anytime.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        {isActive && (
          <Button variant="text" onClick={() => navigate(-1)}>
            ← Back
          </Button>
        )}
      </Stack>
    </Box>
  );
}

export default Subscribe;
